// Admin -> People (beta gate): every account, who's waiting, balances and grants. Nobody uses the studio until an
// admin lets them in: Approve (with credits), an invite link (Invites), or Restore after a suspension. Give credits
// works on anyone at any time. GET /api/admin/people (+ /:id), POST .../approve | grant | suspend | restore.
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Fold } from "../components/Fold";
import { ledgerSummary } from "../foldtext";
import { api } from "../api";
import { setTrusted } from "../pipeline";
import { Empty, ErrorNote, fmtCredits, Icon, Sheet, Spinner, timeAgo, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import { toast, useStore } from "../store";
import type { Access, PeopleCounts, Person, PersonLedgerEntry } from "../types";
import "./people.css";

type Filter = Access | "all";
const FILTERS: { id: Filter; label: string; count: (c: PeopleCounts) => number }[] = [
  { id: "all", label: "Everyone", count: (c) => c.total },
  { id: "pending", label: "Waiting", count: (c) => c.pending },
  { id: "active", label: "Active", count: (c) => c.active },
  { id: "suspended", label: "Suspended", count: (c) => c.suspended },
];
const ACCESS: Record<Access, { label: string; tone: string }> = {
  pending: { label: "Waiting", tone: "busy" },
  active: { label: "Active", tone: "done" },
  suspended: { label: "Suspended", tone: "bad" },
};
// Wave 3: no starter credits; an admin gives them by hand. The api's grantPresets (core GRANT_PRESETS) are the named
// one-tap amounts: 1,500 for a beta tester, 5,000 for a power tester; the field takes any other amount.
type Preset = { credits: number; label?: string };
const NAMED: Preset[] = [
  { credits: 1500, label: "Beta tester" },
  { credits: 5000, label: "Power tester" },
];
const approvePresets = (named: Preset[]): Preset[] => [{ credits: 0, label: "No credits" }, ...named];
const grantPresets = (named: Preset[]): Preset[] => [{ credits: 100 }, { credits: 500 }, ...named];
const MAX_GRANT = 1_000_000;

/** One key per Give: a double click or a retried request becomes one grant on the api. */
function newKey() {
  try {
    return crypto.randomUUID();
  } catch {
    // http on a LAN address has no randomUUID; getRandomValues still works there
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
  }
}

const shortDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: new Date(iso).getFullYear() === new Date().getFullYear() ? undefined : "numeric" }) : "");
const display = (p: Person) => p.name.trim() || p.email.split("@")[0];
const plural = (n: number, w: string) => `${fmtCredits(n)} ${w}${n === 1 ? "" : "s"}`;

type Sheet_ = { kind: "approve" | "grant" | "suspend" | "detail"; person: Person } | null;

export function PeopleScreen() {
  const me = useStore((s) => s.me);
  const admin = me?.user.role === "admin";
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  // search as you type, without a request per key
  useEffect(() => {
    const t = window.setTimeout(() => setTerm(q.trim()), 220);
    return () => window.clearTimeout(t);
  }, [q]);
  const { data, setData, error, loading, reload } = useLoad(() => (admin ? api.people({ q: term || undefined, access: filter === "all" ? undefined : filter }) : Promise.resolve(null)), [admin, term, filter]);
  const [sheet, setSheet] = useState<Sheet_>(null);

  if (!admin)
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-primary">Back home</Link>}>
            People are managed by the studio's admins.
          </Empty>
        </div>
      </Shell>
    );

  /** Puts the api's fresh copy of a person into the list (and the open sheet), and re-counts. */
  const update = (p: Person) => {
    setData((d) => {
      if (!d) return d;
      const counts = { ...d.counts };
      const before = d.people.find((x) => x.id === p.id)?.access;
      if (before && before !== p.access) {
        counts[before] -= 1;
        counts[p.access] += 1;
      }
      const people = d.people.map((x) => (x.id === p.id ? p : x)).filter((x) => filter === "all" || x.access === filter);
      return { ...d, counts, people };
    });
    setSheet((s) => (s && s.person.id === p.id ? { ...s, person: p } : s));
  };

  const done = (p: Person) => {
    update(p);
    setSheet(null);
  };
  const restore = async (p: Person) => {
    try {
      update(await api.restorePerson(p.id));
      toast(`${display(p)} is back in.`, "good");
    } catch (e) {
      toast(`Couldn't restore them. ${(e as Error).message}`, "bad");
    }
  };

  const counts = data?.counts;
  const trial = data?.trialCredits ?? 0;
  const named = data?.grantPresets?.length ? data.grantPresets : NAMED;
  return (
    <Shell>
      <div className="page people">
        <header className="page-head">
          <Link to="/settings" className="back">
            <Icon name="back" size={18} /> Settings
          </Link>
          <h1 className="page-title">People</h1>
          <p className="page-lede">
            Nobody gets in until you let them. Approve who's waiting, give credits whenever someone asks, and pause an account if you need to. Invite links are in <Link to="/admin/invites">Invites</Link>.
          </p>
        </header>

        <div className="people-tools">
          <label className="search">
            <Icon name="search" size={18} />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name" aria-label="Search people by email or name" autoComplete="off" spellCheck={false} inputMode="email" />
          </label>
          <div className="filters" role="group" aria-label="Show">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" aria-pressed={filter === f.id} className={`chip${filter === f.id ? " is-on" : ""}${f.id === "pending" && counts?.pending ? " chip-alert" : ""}`} onClick={() => setFilter(f.id)}>
                {f.label}
                {counts && <span className="chip-count">{f.count(counts)}</span>}
              </button>
            ))}
          </div>
        </div>

        <section aria-label="People" className="people-list">
          {error && <ErrorNote text={`Couldn't load people. ${error}`} retry={reload} />}
          {loading && !data && <Spinner label="Loading people" />}
          {data && !data.people.length && (
            <p className="muted people-empty">{term ? `Nobody matches "${term}".` : filter === "pending" ? "Nobody is waiting. People who sign up without an invite show up here." : "Nobody here yet."}</p>
          )}
          {!!data?.people.length && (
            <ul className="ppl-rows">
              {data.people.map((p) => (
                <PersonRow key={p.id} p={p} self={p.id === me?.user.id} onOpen={() => setSheet({ kind: "detail", person: p })} onApprove={() => setSheet({ kind: "approve", person: p })} onGrant={() => setSheet({ kind: "grant", person: p })} onRestore={() => void restore(p)} />
              ))}
            </ul>
          )}
        </section>

        {sheet?.kind === "approve" && <ApproveSheet person={sheet.person} trial={trial} presets={approvePresets(named)} onClose={() => setSheet(null)} onDone={done} />}
        {sheet?.kind === "grant" && <GrantSheet person={sheet.person} presets={grantPresets(named)} onClose={() => setSheet(null)} onDone={done} />}
        {sheet?.kind === "suspend" && <SuspendSheet person={sheet.person} onClose={() => setSheet(null)} onDone={done} />}
        {sheet?.kind === "detail" && (
          <DetailSheet
            person={sheet.person}
            self={sheet.person.id === me?.user.id}
            onClose={() => setSheet(null)}
            onAction={(kind) => (kind === "restore" ? void restore(sheet.person) : setSheet({ kind, person: sheet.person }))}
          />
        )}
      </div>
    </Shell>
  );
}

// ------------------------------------------------------------------ a row

function PersonRow({ p, self, onOpen, onApprove, onGrant, onRestore }: { p: Person; self: boolean; onOpen: () => void; onApprove: () => void; onGrant: () => void; onRestore: () => void }) {
  const last = p.recentGrants[0];
  const asked = p.requestedAt && p.access !== "active";
  return (
    <li className={`ppl-row ppl-${p.access}`}>
      <button type="button" className="ppl-main" onClick={onOpen} aria-label={`${display(p)}, ${p.email}. ${ACCESS[p.access].label}. ${plural(p.balance, "credit")}. Open details`}>
        <span className="ppl-avatar" aria-hidden>
          {display(p).slice(0, 1).toUpperCase()}
        </span>
        <span className="ppl-id">
          <span className="ppl-name">
            {display(p)}
            {p.role === "admin" && <span className="ppl-tag">admin</span>}
            {self && <span className="ppl-tag">you</span>}
          </span>
          <span className="ppl-email">{p.email}</span>
          <span className="ppl-dates">
            Joined {shortDate(p.signedUpAt)}
            {p.lastActiveAt ? ` · seen ${timeAgo(p.lastActiveAt)}` : " · not seen yet"}
          </span>
        </span>
      </button>
      <span className={`status status-${ACCESS[p.access].tone} ppl-status`}>{ACCESS[p.access].label}</span>
      <span className="ppl-balance" aria-label={plural(p.balance, "credit")}>
        <strong>{fmtCredits(p.balance)}</strong>
        <span>credits</span>
      </span>

      {(asked || last) && (
        <div className="ppl-notes">
          {asked && (
            <p className="ppl-ask">
              <span className="ppl-ask-when">{p.access === "suspended" ? "Asked to come back" : "Asked for access"} {timeAgo(p.requestedAt!)}</span>
              {p.note && <q>{p.note}</q>}
            </p>
          )}
          {last && (
            <p className="ppl-grant">
              <span className="ppl-plus">+{fmtCredits(last.credits)}</span> {last.kind === "approval" ? "on approval" : "given"} {timeAgo(last.createdAt)}
              {last.note ? <span className="ppl-grant-note"> · {last.note}</span> : null}
              {p.recentGrants.length > 1 && <span className="ppl-more"> · {p.recentGrants.length - 1} more</span>}
            </p>
          )}
        </div>
      )}

      <div className="ppl-acts">
        {p.access === "pending" && (
          <button type="button" className="btn btn-primary btn-small" onClick={onApprove}>
            <Icon name="check" size={16} /> Approve
          </button>
        )}
        {p.access === "suspended" && (
          <button type="button" className="btn btn-primary btn-small" onClick={onRestore}>
            <Icon name="restore" size={16} /> Restore
          </button>
        )}
        <button type="button" className="btn btn-quiet btn-small" onClick={onGrant}>
          <Icon name="credits" size={16} /> Give credits
        </button>
        <TrustedToggle p={p} />
      </div>
    </li>
  );
}

// 2026-11-05 (feedback pipeline): a trusted reporter's small improvements ship without Keaton's OK
function TrustedToggle({ p }: { p: Person }) {
  const [on, setOn] = useState(!!p.trusted);
  const [busy, setBusy] = useState(false);
  useEffect(() => setOn(!!p.trusted), [p.trusted]);
  const flip = async (next: boolean) => {
    setOn(next);
    setBusy(true);
    try {
      await setTrusted(p.id, next);
    } catch (e) {
      setOn(!next);
      toast(`Couldn't save that. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="ppl-trust" title="Their small improvements ship without your OK">
      <input type="checkbox" checked={on} disabled={busy} onChange={(e) => void flip(e.target.checked)} />
      Trusted
    </label>
  );
}

// ------------------------------------------------------------------ sheets

function AmountField({ value, onChange, presets, label }: { value: string; onChange: (v: string) => void; presets: Preset[]; label: string }) {
  return (
    <div className="field">
      <label className="field-label" htmlFor="ppl-amount">
        {label}
      </label>
      <input id="ppl-amount" type="number" min={0} max={MAX_GRANT} step={50} value={value} onChange={(e) => onChange(e.target.value)} inputMode="numeric" data-autofocus />
      <div className="ppl-presets" role="group" aria-label="Quick amounts">
        {presets.map((p) => (
          <button key={p.credits} type="button" className={`chip${value !== "" && Number(value) === p.credits ? " is-on" : ""}`} onClick={() => onChange(String(p.credits))} data-preset={p.credits}>
            {p.label && p.credits ? `${fmtCredits(p.credits)} · ${p.label}` : (p.label ?? fmtCredits(p.credits))}
          </button>
        ))}
      </div>
    </div>
  );
}

function NoteField({ value, onChange, hint }: { value: string; onChange: (v: string) => void; hint: string }) {
  return (
    <label className="field">
      <span className="field-label">Note (optional)</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} maxLength={300} placeholder={hint} autoComplete="off" />
      <span className="field-hint">Only admins see it, in this person's history.</span>
    </label>
  );
}

function Who({ p }: { p: Person }) {
  return (
    <p className="ppl-who">
      <strong>{display(p)}</strong> <span className="muted">{p.email}</span>
      <span className="muted">Balance {plural(p.balance, "credit")}</span>
    </p>
  );
}

const amountOf = (v: string, min: number) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= MAX_GRANT ? n : null;
};

function ApproveSheet({ person, trial, presets, onClose, onDone }: { person: Person; trial: number; presets: Preset[]; onClose: () => void; onDone: (p: Person) => void }) {
  const [credits, setCredits] = useState(String(trial));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const n = amountOf(credits, 0);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (n === null || busy) return;
    setBusy(true);
    try {
      onDone(await api.approvePerson(person.id, { credits: n, note: note.trim() || null }));
      toast(`${display(person)} is in${n ? ` with ${plural(n, "credit")}` : ""}.`, "good");
    } catch (err) {
      toast(`Couldn't approve them. ${(err as Error).message}`, "bad");
      setBusy(false);
    }
  };
  return (
    <Sheet title="Let them in" onClose={onClose}>
      <form className="sheet-body" onSubmit={submit}>
        <Who p={person} />
        {person.note && <q className="ppl-quote">{person.note}</q>}
        <AmountField label="Starting credits" value={credits} onChange={setCredits} presets={presets} />
        <NoteField value={note} onChange={setNote} hint="Who they are, why they're in" />
        {n === null && <p className="form-error">A whole number from 0 to {fmtCredits(MAX_GRANT)}.</p>}
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Not now
          </button>
          <button type="submit" className="btn btn-primary" disabled={n === null || busy}>
            {busy ? "Letting them in" : n ? `Approve with ${fmtCredits(n)}` : "Approve, no credits"}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function GrantSheet({ person, presets, onClose, onDone }: { person: Person; presets: Preset[]; onClose: () => void; onDone: (p: Person) => void }) {
  const [credits, setCredits] = useState("1500");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // one key for this sheet: any number of taps or retries on Give is one grant
  const key = useMemo(newKey, []);
  const n = amountOf(credits, 1);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (n === null || busy) return;
    setBusy(true);
    try {
      const r = await api.grantCredits(person.id, { credits: n, note: note.trim() || null, idempotencyKey: key });
      onDone(r.person);
      toast(r.replayed ? "Already given. Nothing was added twice." : `Gave ${display(person)} ${plural(n, "credit")}.`, "good");
    } catch (err) {
      toast(`Couldn't give the credits. ${(err as Error).message}`, "bad");
      setBusy(false); // same key on a retry: still one grant
    }
  };
  return (
    <Sheet title="Give credits" onClose={onClose}>
      <form className="sheet-body" onSubmit={submit}>
        <Who p={person} />
        <AmountField label="Credits to give" value={credits} onChange={setCredits} presets={presets} />
        <NoteField value={note} onChange={setNote} hint="What it's for, e.g. asked for more for a launch" />
        {n === null ? (
          <p className="form-error">A whole number from 1 to {fmtCredits(MAX_GRANT)}.</p>
        ) : (
          <p className="sheet-note">
            Their balance goes to {fmtCredits(person.balance + n)}. {person.access === "active" ? "" : "Giving credits doesn't let them in."}
          </p>
        )}
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={n === null || busy}>
            {busy ? "Giving" : n ? `Give ${fmtCredits(n)}` : "Give"}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function SuspendSheet({ person, onClose, onDone }: { person: Person; onClose: () => void; onDone: (p: Person) => void }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      onDone(await api.suspendPerson(person.id));
      toast(`${display(person)}'s access is paused.`, "good");
    } catch (err) {
      toast(`Couldn't suspend them. ${(err as Error).message}`, "bad");
      setBusy(false);
    }
  };
  return (
    <Sheet title="Suspend access?" onClose={onClose}>
      <div className="sheet-body confirm">
        <Who p={person} />
        <p>They lose access right away: the next thing they do shows a "your access is paused" page. Their projects and credits stay as they are, and they can't get back in with an invite or a purchase. Restore lets them back in any time.</p>
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Keep access
          </button>
          <button type="button" className="btn btn-primary btn-danger" onClick={() => void go()} disabled={busy} data-autofocus>
            {busy ? "Suspending" : "Suspend"}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

const REASON: Record<string, string> = { grant: "Given", invite: "Invite", purchase: "Bought", refund: "Refund", script: "Script", render: "Render" };

function DetailSheet({ person: p, self, onClose, onAction }: { person: Person; self: boolean; onClose: () => void; onAction: (k: "approve" | "grant" | "suspend" | "restore") => void }) {
  const { data, error, reload } = useLoad<{ person: Person; ledger: PersonLedgerEntry[] }>(() => api.person(p.id), [p.id, p.balance, p.access]);
  const ledger = data?.ledger;
  return (
    <Sheet title={display(p)} onClose={onClose} wide>
      <div className="sheet-body ppl-detail">
        <div className="ppl-detail-head">
          <span className={`status status-${ACCESS[p.access].tone} ppl-status`}>{ACCESS[p.access].label}</span>
          <p className="ppl-detail-bal">
            <strong>{fmtCredits(p.balance)}</strong> credits
          </p>
        </div>
        <dl className="ppl-facts">
          <div>
            <dt>Email</dt>
            <dd>{p.email}</dd>
          </div>
          <div>
            <dt>Signed up</dt>
            <dd>{shortDate(p.signedUpAt)}</dd>
          </div>
          <div>
            <dt>Last active</dt>
            <dd>{p.lastActiveAt ? timeAgo(p.lastActiveAt) : "Not yet"}</dd>
          </div>
          {p.approvedAt && (
            <div>
              <dt>Let in</dt>
              <dd>{shortDate(p.approvedAt)}</dd>
            </div>
          )}
          {p.suspendedAt && (
            <div>
              <dt>Suspended</dt>
              <dd>{shortDate(p.suspendedAt)}</dd>
            </div>
          )}
        </dl>
        {p.note && (
          <div className="ppl-said">
            <p className="panel-label">{p.requestedAt ? `They wrote, ${timeAgo(p.requestedAt)}` : "They wrote"}</p>
            <q className="ppl-quote">{p.note}</q>
          </div>
        )}

        <div className="ppl-detail-acts">
          {p.access === "pending" && (
            <button type="button" className="btn btn-primary" onClick={() => onAction("approve")}>
              <Icon name="check" size={18} /> Approve
            </button>
          )}
          {p.access === "suspended" && (
            <button type="button" className="btn btn-primary" onClick={() => onAction("restore")}>
              <Icon name="restore" size={18} /> Restore access
            </button>
          )}
          <button type="button" className="btn btn-quiet" onClick={() => onAction("grant")}>
            <Icon name="credits" size={18} /> Give credits
          </button>
          <Link to={`/admin/people/${p.id}`} className="btn btn-quiet" onClick={onClose}>
            <Icon name="sliders" size={18} /> Spend
          </Link>
          {p.access !== "suspended" && p.role !== "admin" && !self && (
            <button type="button" className="btn btn-quiet ppl-suspend" onClick={() => onAction("suspend")}>
              Suspend
            </button>
          )}
        </div>

        {/* v4 (V4-P2): an admin opens the history when they need it; its row shows the latest entry */}
        <Fold id="person.history" title="Credit history" count={ledger?.length || undefined} summary={ledger ? ledgerSummary(ledger) : ""} className="ppl-hist">
          {error && <ErrorNote text={`Couldn't load the history. ${error}`} retry={reload} />}
          {!ledger && !error && <Spinner label="Loading" />}
          {ledger && !ledger.length && <p className="muted">No credits in or out yet.</p>}
          {!!ledger?.length && (
            <ul className="ppl-ledger">
              {ledger.map((l) => (
                <li key={l.id}>
                  <span className={`ppl-delta${l.delta > 0 ? " is-in" : ""}`}>
                    {l.delta > 0 ? "+" : "−"}
                    {fmtCredits(Math.abs(l.delta))}
                  </span>
                  <span className="ppl-ledger-what">
                    <span>
                      {l.reason === "grant" && l.ref === "approval" ? "On approval" : (REASON[l.reason] ?? l.reason.replace(/^./, (c) => c.toUpperCase()))}
                      {l.by ? <span className="muted"> by {l.by}</span> : null}
                    </span>
                    {l.note && <span className="ppl-ledger-note">{l.note}</span>}
                  </span>
                  <span className="ppl-ledger-when">{timeAgo(l.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Fold>
      </div>
    </Sheet>
  );
}
